import * as React from "react";
import { useMemo } from "react";

import { CoachSettings, MenuElement } from "@/interfaces";
import { usePlayerSettings, URLS, useAccountSettings, useSocialCoachSession } from "@/utils";
import { SideBarBase } from "../SideBarBase";

import { playerWebOptions, webComponents } from "./components";
import "./styles.scss";

interface Props {
  history: any;
  readyToContinue?: boolean;
}

const SideBarFC: React.FC<Props> = ({ readyToContinue = true, history }) => {
  const { isPlayer, isEditor, playerId, session } = useSocialCoachSession();

  const { hasReadPermission } = useAccountSettings();
  const { hasSetting } = usePlayerSettings(playerId);
  const hasCalendar = useMemo(() => {
    return hasSetting(CoachSettings.PLAYER_CALENDAR_ACCESS);
  }, [hasSetting]);

  const hasPlayerCard = useMemo(() => {
    return hasSetting(CoachSettings.ALLOW_PLAYER_ACCESS_TO_PLAYER_CARDS);
  }, [hasSetting]);

  const hasReputation = useMemo(() => {
    return hasSetting(CoachSettings.PLAYER_REPUTATION_MANAGEMENT);
  }, [hasSetting]);

  const sideMenu = useMemo((): MenuElement[] => {
    if (isEditor) {
      return [
        {
          title: "Editing Queue",
          path: URLS.editor.editQueueList,
        },
        {
          title: "Script Library",
          path: URLS.editor.videoScripts,
        },
      ];
    } else if (isPlayer) {
      const options = playerWebOptions.filter(option => {
        return (
          (option.path !== URLS.player.calendar || hasCalendar) &&
          (option.path !== URLS.player.playerCards || hasPlayerCard) &&
          (!option.path.includes("/reputation") || hasReputation)
        );
      });

      if (session?.subscription?.hasPaymentPlan) {
        options.push({
          title: "Payment Information",
          path: URLS.player.paymentInfo,
        });
      }

      return options;
    } else {
      const coachMenu: MenuElement[] = [
        {
          title: "Dashboard",
          path: URLS.coach.dashboard,
        },
      ];

      webComponents.forEach(component => {
        if (hasReadPermission(component.componentIds)) {
          coachMenu.push(component);
        }
      });

      coachMenu.push({
        title: "Resource Hub",
        path: URLS.coach.help,
        externalLink: true,
      });

      return coachMenu;
    }
  }, [hasReadPermission, isPlayer, isEditor, hasCalendar, hasPlayerCard, session?.subscription?.hasPaymentPlan]);

  return <SideBarBase history={history} menu={sideMenu} readyToContinue={readyToContinue} />;
};

export const SideBar = SideBarFC;
